import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { CalendarDays, Check, ChevronDown, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useElectionContext } from './context'

/**
 * The election selector, for the shell's sub-bar.
 *
 * ## What it replaces
 *
 * The sub-bar said `Election Cycle: 2027 General Election` as hardcoded text. The slot was
 * right and the content was a lie — it said the same thing regardless of what the platform
 * held or what the user was looking at.
 *
 * ## Why the phase is on the row and not only in the dropdown
 *
 * A closed election and one in POLLING look identical by name, and reading a live board
 * while accidentally scoped to last cycle is the mistake this exists to prevent. The phase
 * rides alongside the name at all times.
 *
 * ## The panel is `position: fixed`, and it has to be
 *
 * The sub-bar it sits in carries `overflow-x-auto` so it can scroll on a narrow screen —
 * and **an `overflow` ancestor clips an absolutely-positioned descendant.** The bar is 36px
 * tall, so an `absolute` panel was cropped to a sliver of itself no matter what z-index it
 * carried: z-index orders things that are painted, and this was never painted. Setting
 * `overflow-x` also computes `overflow-y` to `auto` rather than leaving it `visible`, which
 * is the part that surprises people.
 *
 * A fixed panel escapes that clip because it is positioned against the VIEWPORT, so no
 * ancestor's overflow applies. It works here because nothing on the chain above sets
 * `transform`, `filter`, `perspective` or `contain` — any of which would make that ancestor
 * the containing block and reintroduce the clip. (`.axis-sidebar` does use a transform below
 * `lg`, but it is not an ancestor of this bar.)
 *
 * Chosen over a React portal because the DOM parentage stays put: the click-outside handler
 * below still sees the panel inside `box`, so dismissal keeps working with no extra wiring.
 * There is no portal anywhere in this codebase and `Modal` positions itself the same way.
 */
/** `w-72`. A definite width so the panel can be clamped inside the viewport. */
const PANEL_WIDTH = 288
const GAP = 4
const MARGIN = 8

export function ElectionSelector() {
  const { elections, election, select, loading, error } = useElectionContext()
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement>(null)
  const trigger = useRef<HTMLButtonElement>(null)
  const [at, setAt] = useState<{ top: number; left: number; maxHeight: number } | null>(null)

  /**
   * Where the panel goes, in viewport coordinates.
   *
   * Clamped on both axes: on a narrow screen the trigger can sit close enough to the right
   * edge that a 288px panel would hang off it, and the height is whatever is left below the
   * bar so a long list scrolls inside itself rather than running past the bottom of the page.
   */
  const place = useCallback(() => {
    const b = trigger.current?.getBoundingClientRect()
    if (!b) return
    setAt({
      top: b.bottom + GAP,
      left: Math.max(MARGIN, Math.min(b.left, window.innerWidth - PANEL_WIDTH - MARGIN)),
      maxHeight: Math.max(120, window.innerHeight - b.bottom - GAP - MARGIN),
    })
  }, [])

  // Before paint, so the panel never appears at the wrong place first.
  useLayoutEffect(() => { if (open) place() }, [open, place])

  useEffect(() => {
    if (!open) return
    const reposition = () => place()
    window.addEventListener('resize', reposition)
    /*
       `capture: true` is load-bearing. A scroll event does not bubble, and the element that
       moves the trigger is the SUB-BAR scrolling horizontally — not the window. Listening in
       the capture phase catches a scroll on any ancestor, so the panel follows its trigger
       instead of detaching from it.
    */
    window.addEventListener('scroll', reposition, true)
    return () => {
      window.removeEventListener('resize', reposition)
      window.removeEventListener('scroll', reposition, true)
    }
  }, [open, place])

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDown)
    window.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      window.removeEventListener('keydown', onKey)
    }
  }, [open])

  if (loading && elections.length === 0) {
    return (
      <span className="inline-flex items-center gap-1.5 text-muted-foreground whitespace-nowrap">
        <Loader2 className="h-3 w-3 animate-spin" /> Loading elections…
      </span>
    )
  }

  // A refusal is named. An empty dropdown reading "no elections" when the real answer is
  // "you may not list them" sends somebody hunting for missing data that is right there.
  if (error) {
    return (
      <span className="text-amber-800 dark:text-amber-400 whitespace-nowrap" title={error}>
        Elections unavailable
      </span>
    )
  }

  return (
    <div ref={box} className="relative">
      <button ref={trigger} type="button" onClick={() => setOpen((o) => !o)}
        aria-expanded={open} aria-haspopup="listbox"
        className={cn(
          'inline-flex items-center gap-1.5 h-6 px-2 rounded-md border text-erp-sm whitespace-nowrap',
          election
            ? 'border-transparent hover:border-border font-medium'
            : 'border-amber-400 text-amber-800 dark:text-amber-400',
        )}>
        <CalendarDays className="h-3 w-3 opacity-70" />
        {election ? election.name : 'Choose an election'}
        {election && (
          <span className="text-erp-xs text-muted-foreground font-normal">
            · {phaseLabel(election.statusCode)}
          </span>
        )}
        <ChevronDown className="h-3 w-3 opacity-60" />
      </button>

      {open && at && (
        <div
          style={{ top: at.top, left: at.left, maxHeight: at.maxHeight, width: PANEL_WIDTH }}
          className="fixed z-50 overflow-y-auto erp-card shadow-lg py-1"
          role="listbox">
          {elections.length === 0 && (
            <p className="px-3 py-3 text-erp-xs text-muted-foreground">
              No elections have been created yet. One is needed before candidates can be
              nominated or votes tallied.
            </p>
          )}
          {elections.map((e) => (
            <button key={e.id} type="button"
              onClick={() => { select(e.id); setOpen(false) }}
              className="w-full text-left px-3 py-2 hover:bg-muted/60 flex items-start gap-2">
              <Check className={cn('h-3.5 w-3.5 mt-0.5 shrink-0',
                election?.id === e.id ? 'opacity-100 text-chocolate' : 'opacity-0')} />
              <span className="min-w-0">
                <span className="block text-erp-sm truncate">{e.name}</span>
                <span className="block text-erp-xs text-muted-foreground">
                  {new Date(e.pollingDate).toLocaleDateString()} · {phaseLabel(e.statusCode)}
                  {e.electionType && ` · ${typeLabel(e.electionType)}`}
                </span>
              </span>
            </button>
          ))}
          {election && (
            <button type="button"
              onClick={() => { select(null); setOpen(false) }}
              className="w-full text-left px-3 py-2 mt-1 border-t border-border text-erp-xs text-muted-foreground hover:bg-muted/60">
              Clear the selection
            </button>
          )}
        </div>
      )}
    </div>
  )
}

const PHASES: Record<string, string> = {
  PLANNED: 'Planned', ORGANISING: 'Organising', ENGAGING: 'Engaging',
  POLLING: 'Polling day', CLOSED: 'Closed', RECONCILED: 'Reconciled',
}
const phaseLabel = (code?: string | null) =>
  !code ? '—' : (PHASES[code] ?? title(code))

const TYPES: Record<string, string> = {
  GENERAL: 'General', BY_ELECTION: 'By-election', REFERENDUM: 'Referendum',
  PRIMARY: 'Party primary',
}
const typeLabel = (code: string) => TYPES[code] ?? title(code)

const title = (c: string) => c.charAt(0) + c.slice(1).toLowerCase().replace(/_/g, ' ')

/**
 * What an election-scoped page renders when nothing is selected.
 *
 * A sentence, not an empty grid: "no rows" and "no election chosen" look identical
 * otherwise, and the second one is the user's to fix in one click.
 */
export function NoElectionSelected({ what }: { what: string }) {
  return (
    <div className="erp-card px-4 py-10 text-center">
      <div className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-amber-50 text-amber-700 border border-amber-200 mb-3">
        <CalendarDays className="h-4 w-4" />
      </div>
      <div className="text-erp-sm font-medium">No election selected</div>
      <div className="text-erp-xs text-muted-foreground mt-1 max-w-sm mx-auto">
        {what} belongs to one election. Choose one in the bar above — the choice is
        remembered and travels in the page's link.
      </div>
    </div>
  )
}
